import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  HrailWaterproofKit,
  InsertHrailWaterproofKitBody,
  UpdateHrailWaterproofKitBody,
} from "./types/waterproofKits";

export const useHrailWaterproofKitsApi = () => {
  const api = useApi();

  const getAdminHrailWaterproofKit = useCallback(
    (waterproofKitId: string, signal: AbortSignal) => {
      const url = `/admin/settings/hrail/waterproof_kits/${waterproofKitId}`;
      return useSWRMutation<HrailWaterproofKit, CustomError, string>(url, (url) =>
        api.get<HrailWaterproofKit>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminHrailWaterproofKits = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/waterproof_kits`, query);
      return useSWRImmutable<Paginated<HrailWaterproofKit>, CustomError, string>(url, (url) =>
        api.get<Paginated<HrailWaterproofKit>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminHrailWaterproofKits = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/waterproof_kits`, query);
      return api.get<Paginated<HrailWaterproofKit>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminHrailWaterproofKit = useCallback(
    async (body: InsertHrailWaterproofKitBody): Promise<{ id: string }> => {
      const id = await api.post<{ id: string }, InsertHrailWaterproofKitBody>(
        `/admin/settings/hrail/waterproof_kits`,
        body,
      );
      mutateCache([`/admin/settings/hrail/waterproof_kits`, `/settings/hrail`]);
      return id;
    },
    [api],
  );

  const updateAdminHrailWaterproofKit = useCallback(
    async (waterproofKitId: string, body: UpdateHrailWaterproofKitBody): Promise<void> => {
      const url = `/admin/settings/hrail/waterproof_kits/${waterproofKitId}`;
      await api.put<UpdateHrailWaterproofKitBody>(url, body);
      mutateCache([`/admin/settings/hrail/waterproof_kits`, `/settings/hrail`], url);
    },
    [api],
  );

  const updateAdminHrailWaterproofKitVisibility = useCallback(
    async (waterproofKitId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/waterproof_kits/${waterproofKitId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([`/admin/settings/hrail/waterproof_kits`, `/settings/hrail`]);
    },
    [api],
  );

  const updateAdminHrailWaterproofKitOrder = useCallback(
    async (waterproofKitId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/waterproof_kits/${waterproofKitId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache([`/admin/settings/hrail/waterproof_kits`, `/settings/hrail`]);
    },
    [api],
  );

  const deleteAdminHrailWaterproofKit = useCallback(
    async (waterproofKitId: string): Promise<void> => {
      await api.del(`/admin/settings/hrail/waterproof_kits/${waterproofKitId}`);
      mutateCache([`/admin/settings/hrail/waterproof_kits`, `/settings/hrail`]);
    },
    [api],
  );

  return {
    getAdminHrailWaterproofKit,
    useAdminHrailWaterproofKits,
    getAdminHrailWaterproofKits,
    insertAdminHrailWaterproofKit,
    updateAdminHrailWaterproofKit,
    updateAdminHrailWaterproofKitVisibility,
    updateAdminHrailWaterproofKitOrder,
    deleteAdminHrailWaterproofKit,
  };
};
